import { useEffect, useState } from "react";
import { Gift, Loader2, Timer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg, fmtDate } from "@/components/attendance/ui";
import BillingReadiness from "./BillingReadiness";
import PlanPrices from "./PlanPrices";
import CompDialog from "./CompDialog";

const PLAN = { attendance: "Attendance", hymn_projection: "Hymn Projection", church_bundle: "Bundle" };
const REASON = { trialing: "Free trial", active: "Active", complimentary: "Complimentary", grace: "Grace period", none: "No subscription",
  not_included: "Not in plan", canceled: "Cancelled", past_due: "Payment overdue", unpaid: "Unpaid", incomplete: "Incomplete" };

export function OrgTable({ orgs, product, onDays }) {
  const key = product === "projection" ? "projection_access" : "access";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400"><th className="text-left py-3">Organisation</th><th className="text-left">Owner</th><th className="text-left">Plan</th><th className="text-left">Status</th><th className="text-left">Until</th>{product === "attendance" && <th className="text-right">Events</th>}<th className="text-right">Actions</th></tr></thead>
        <tbody>
          {orgs.map((o) => (
            <tr key={o.id} className="border-b border-white/5" data-testid={`${product}-admin-org-${o.id}`}>
              <td className="py-3 text-white">{o.name}<div className="text-[11px] text-neutral-500">{o.org_type} · since {fmtDate(o.created_at)}</div></td>
              <td className="text-neutral-400">{o.owner_email}</td>
              <td className="text-neutral-300">{PLAN[o.subscription_plan] || "—"}</td>
              <td><span className={o[key].has_access ? "text-emerald-300" : "text-neutral-500"} data-testid={`${product}-admin-status-${o.id}`}>{REASON[o[key].reason] || o[key].reason}</span></td>
              <td className="text-neutral-400">{o[key].until?.startsWith("9999") ? "Permanent" : fmtDate(o[key].until)}</td>
              {product === "attendance" && <td className="text-right">{o.event_count}</td>}
              <td className="text-right whitespace-nowrap">
                <button onClick={() => onDays(o, "comp")} title="Complimentary access" data-testid={`${product}-admin-comp-${o.id}`} className="p-2 text-neutral-400 hover:text-[#D4AF37]"><Gift size={15} /></button>
                <button onClick={() => onDays(o, "extend-trial")} title="Extend Stripe trial" data-testid={`${product}-admin-extend-${o.id}`} className="p-2 text-neutral-400 hover:text-[#D4AF37]"><Timer size={15} /></button>
              </td>
            </tr>
          ))}
          {!orgs.length && <tr><td colSpan={7} className="py-6 text-center text-neutral-500">No organisations yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function useOrgs(product) {
  const [orgs, setOrgs] = useState(null);
  const [comp, setComp] = useState(null);
  const load = () => api.get("/admin/attendance/orgs").then((r) => setOrgs(r.data)).catch(() => toast.error("Could not load organisations"));
  useEffect(() => { load(); }, []);
  const onDays = async (o, kind) => {
    if (kind === "comp") { setComp(o); return; }
    const v = window.prompt("Extend the Stripe trial by how many days?", "30");
    if (v == null) return;
    try { await api.post(`/admin/attendance/orgs/${o.id}/${kind}`, { days: Number(v) }); toast.success("Updated"); load(); }
    catch (e) { toast.error(errMsg(e, "Update failed")); }
  };
  const dialog = comp && <CompDialog org={comp} product={product} onClose={() => setComp(null)} onDone={() => { setComp(null); load(); }} />;
  return { orgs, onDays, dialog };
}

export default function AttendanceAdmin() {
  const { orgs, onDays, dialog } = useOrgs("attendance");
  if (!orgs) return <Loader2 className="animate-spin text-[#D4AF37] mt-8" />;
  return (
    <div className="mt-8 space-y-10" data-testid="attendance-admin">
      <BillingReadiness />
      <PlanPrices />
      <section>
        <h3 className="font-serif-display text-2xl text-white mb-4">Organisations ({orgs.length})</h3>
        <OrgTable orgs={orgs} product="attendance" onDays={onDays} />
        {dialog}
      </section>
    </div>
  );
}
